import _ from 'lodash';
import DataSvc from './data';
import Formatters from './formatters';
import EventBus from './event-bus';

export default {
  data() {
    return {
      states: {
        AL: 'Alabama',
        AK: 'Alaska',
        AZ: 'Arizona',
        AR: 'Arkansas',
        CA: 'California',
        CO: 'Colorado',
        CT: 'Connecticut',
        DE: 'Delaware',
        DC: 'District Of Columbia',
        FL: 'Florida',
        GA: 'Georgia',
        HI: 'Hawaii',
        ID: 'Idaho',
        IL: 'Illinois',
        IN: 'Indiana',
        IA: 'Iowa',
        KS: 'Kansas',
        KY: 'Kentucky',
        LA: 'Louisiana',
        ME: 'Maine',
        MD: 'Maryland',
        MA: 'Massachusetts',
        MI: 'Michigan',
        MN: 'Minnesota',
        MS: 'Mississippi',
        MO: 'Missouri',
        MT: 'Montana',
        NE: 'Nebraska',
        NV: 'Nevada',
        NH: 'New Hampshire',
        NJ: 'New Jersey',
        NM: 'New Mexico',
        NY: 'New York',
        NC: 'North Carolina',
        ND: 'North Dakota',
        OH: 'Ohio',
        OK: 'Oklahoma',
        OR: 'Oregon',
        PA: 'Pennsylvania',
        RI: 'Rhode Island',
        SC: 'South Carolina',
        SD: 'South Dakota',
        TN: 'Tennessee',
        TX: 'Texas',
        UT: 'Utah',
        VT: 'Vermont',
        VA: 'Virginia',
        WA: 'Washington',
        WV: 'West Virginia',
        WI: 'Wisconsin',
        WY: 'Wyoming',
      },
    };
  },
  filters: {
    currency(number) {
      return Formatters.currency(number);
    },
    number(number, decimals) {
      return Formatters.number(number, decimals);
    },
    datetime(iso) {
      return Formatters.datetime(iso);
    },
    capitalize(str) {
      return _.startCase(str);
    },
  },
  computed: {
    loggedInUser: {
      get() { return this.$root.$data.user; },
      set(user) { this.$root.$data.user = user; },
    },
    organizations: {
      get() { return _.orderBy(this.$root.$data.orgs, 'name'); },
      set(orgs) { this.$root.$data.orgs = orgs; },
    },
    seasons: {
      get() { return _.orderBy(this.$root.$data.sns, 'timestamp_start_date_time'); },
      set(sns) { this.$root.$data.sns = sns; },
    },
    timezones: {
      get() { return this.$root.$data.tzs; },
      set(tzs) { this.$root.$data.tzs = tzs; },
    },
    initPromise: {
      get() { return this.$root.$data.init; },
      set(init) { this.$root.$data.init = init; },
    },
  },
  methods: {
    eventStatus(event) {
      const status = { canRegister: false };

      if (!event.id) return status;

      const openPricing = _.filter(
        event.pricing_packages,
        { is_open: true },
      );

      // closed due to no pricing
      if (openPricing.length <= 0) {
        status.canRegister = false;
        status.reason = 'Registration Closed (No Active Pricing)';
        status.class = 'danger';

      // active roster open
      } else if (event.registration_open) {
        status.canRegister = true;
        status.reason = 'Active Roster Open';
        status.class = 'success';

      // waitlist open
      } else if (event.waitlist_open) {
        status.canRegister = true;
        status.reason = 'Waitlist Open';
        status.class = 'warning';

      // closed due to full roster
      } else if (event.full_registrations
        && event.full_waitlist) {
        status.canRegister = false;
        status.reason = 'Registration Closed (Full)';
        status.class = 'danger';

      // close due to event over
      } else if (event.event_over) {
        status.canRegister = false;
        status.reason = 'Registration Closed (Event Over)';
        status.class = 'danger';

      // closed due to registration over
      } else {
        status.canRegister = false;
        status.reason = 'Registration Closed (Registration Over)';
        status.class = 'danger';
      }

      return status;
    },
    hasRole(perm) {
      return this.loggedInUser.permissions.includes(perm);
    },
    focusInput(ref) {
      try {
        this.$refs[ref].focus();
      } catch (e) {
        // eslint-disable-next-line
        console.warn(`Could not focus element reference: '${ref}'.`);
      }
    },
    delay(t, val) {
      return new Promise((resolve) => {
        if (t <= 0) {
          resolve(val);
        } else {
          setTimeout(resolve.bind(null, val), t);
        }
      });
    },
    goToEvent(eventID, tab) {
      this.$router.push({
        name: 'event',
        params: {
          eventID,
          tab: tab || 'details',
        },
      });
    },
    goToPricing(eventID, pricingID) {
      this.$router.push({
        name: 'pricing',
        params: { eventID, pricingID },
      });
    },
    goToPromoCode(eventID, promoCodeID) {
      this.$router.push({
        name: 'promo-code',
        params: { eventID, promoCodeID },
      });
    },
    messageEvents(eventIDs) {
      const communication = { eventIDs };
      this.sessionStorageSet('communication', communication);

      // route to communication page
      this.$router.push({
        name: 'event-communication',
      });
    },
    messageEmails(emails) {
      const communication = { emails };
      this.sessionStorageSet('communication', communication);

      // route to communication page
      this.$router.push({
        name: 'new-email-communication',
      });
    },
    resendConfirmationEmail(registrationSessionID) {
      this.showToast('Resending confirmation email...');

      return DataSvc.resendConfirmationEmail(registrationSessionID)
        .then(() => this.delay(1000))
        .then(() => {
          this.showToast('Confirmation email sent!');
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.hideToast();
        });
    },
    goToOrder(registrationSessionUUID) {
      this.$router.push({
        name: 'order',
        params: { registrationSessionUUID },
      });
    },
    goToRegistration(eventID, registrationID) {
      this.$router.push({
        name: 'registrant',
        params: { eventID, registrationID },
      });
    },
    goToEvents() {
      this.$router.push({
        name: 'events',
      });
    },
    goToSeasons() {
      this.$router.push({
        name: 'seasons',
      });
    },
    hasRoleForRoute(pathToCheck, userRoles) {
      const pathToRoles = {
        // events
        '/events/new/details': ['superadmin'],

        // event form
        '/events/:eventID/form/:formID': ['superadmin'],

        // seasons
        '/seasons': ['superadmin'],
        '/seasons/:seasonID': ['superadmin'],

        // pricing
        '/events/:eventID/pricing': ['superadmin'],
        '/events/:eventID/pricing/:pricingID': ['superadmin'],

        // organizations
        '/organizations': ['superadmin'],
        '/organizations/:organizationID': ['superadmin'],

        // organizers
        '/organizers': ['superadmin'],
        '/organizers/:userID': ['superadmin'],
      };

      pathToCheck = pathToCheck.split('/');

      const getRolesForPath = () => {
        let r;

        _.forEach(pathToRoles, (roles, path) => {
          path = path.split('/');

          if (pathToCheck.length === path.length) {
            let subMatch = true;

            _.forEach(path, (p, i) => {
              if (subMatch) {
                subMatch = _.startsWith(p, ':') || p === pathToCheck[i];
              }
            });

            if (subMatch && !r) {
              r = roles;
            }
          }
        });

        return r;
      };

      const matchedRoles = getRolesForPath();

      const hasRole = !matchedRoles || _.intersection(
        matchedRoles,
        userRoles,
      ).length > 0;

      return hasRole;
    },
    findAndSwitchActingOrg(id) {
      const organization = _.find(
        this.organizations,
        { id },
      );

      if (organization) {
        EventBus.$emit('set-acting-org', organization);
      } else {
        this.showWarning(
          'The organization for this data could not be found.',
          'Organization not found',
        );

        this.goToEvents();
      }
    },
  },
};
